/* Leni cart drawer — interactie binnen de drawer.
   Elke wijziging gaat via de AJAX Cart API mét `sections`, zodat Shopify de
   nieuwe drawer-HTML meteen terugstuurt; die zetten we in .drawer__inner.
   <leni-cart> wordt bij elke render opnieuw aangemaakt, dus alle listeners
   hangen aan het element zelf en verdwijnen vanzelf mee. */

(() => {
  if (customElements.get('leni-cart')) return;

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const TIMER_KEY = 'leni-cart-timer-end';
  const recsCache = new Map();

  const storage = {
    get(key) {
      try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { window.sessionStorage.setItem(key, value); } catch (e) { /* privémodus */ }
    },
  };

  class LeniCart extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.adjustShippingForCurrency();
      this.startTimer();
      this.loadRecs();
    }

    disconnectedCallback() {
      clearInterval(this.timerId);
    }

    /* --- Cart API ------------------------------------------------------- */

    async request(url, body) {
      this.classList.add('is-loading');
      this.showError('');
      try {
        const response = await fetch(`${window.Shopify.routes.root}${url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.showError(data.description || data.message || 'Something went wrong. Please try again.');
          return null;
        }
        this.render(data.sections);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'leni-cart', cartData: data });
        }
        return data;
      } catch (error) {
        this.showError('Something went wrong. Please try again.');
        return null;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(sections) {
      if (!sections) return;
      const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);

      const drawerHtml = sections['cart-drawer'];
      if (drawerHtml) {
        const fresh = parse(drawerHtml, '.drawer__inner');
        const current = this.drawer?.querySelector('.drawer__inner');
        if (fresh && current) {
          const empty = parse(drawerHtml, 'cart-drawer')?.classList.contains('is-empty');
          this.drawer.classList.toggle('is-empty', Boolean(empty));
          const scroll = current.querySelector('.leni-cart__scroll')?.scrollTop || 0;
          current.innerHTML = fresh.innerHTML;
          const newScroll = current.querySelector('.leni-cart__scroll');
          if (newScroll) newScroll.scrollTop = scroll;
        }
      }

      const bubbleHtml = sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        const fresh = parse(bubbleHtml, '.shopify-section');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      }
    }

    showError(message) {
      const el = this.querySelector('[data-leni-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    /* --- Events ---------------------------------------------------------- */

    onClick(event) {
      const qty = event.target.closest('[data-leni-qty]');
      if (qty) {
        const line = qty.closest('[data-line]').dataset.line;
        this.request('cart/change.js', { line: Number(line), quantity: Math.max(0, Number(qty.dataset.leniQty)) });
        return;
      }

      const remove = event.target.closest('[data-leni-remove]');
      if (remove) {
        const line = remove.closest('[data-line]').dataset.line;
        this.request('cart/change.js', { line: Number(line), quantity: 0 });
        return;
      }

      const add = event.target.closest('[data-leni-rec-add]');
      if (add) {
        add.disabled = true;
        this.request('cart/add.js', { items: [{ id: Number(add.dataset.leniRecAdd), quantity: 1 }] }).then((data) => {
          if (!data) add.disabled = false;
        });
        return;
      }

      const removeCode = event.target.closest('[data-leni-remove-code]');
      if (removeCode) {
        const codes = this.currentCodes().filter((code) => code !== removeCode.dataset.leniRemoveCode);
        this.request('cart/update.js', { discount: codes.join(',') });
        return;
      }

      const prev = event.target.closest('[data-leni-recs-prev]');
      const next = event.target.closest('[data-leni-recs-next]');
      if (prev || next) {
        const track = this.querySelector('[data-leni-recs-track]');
        if (track) track.scrollBy({ left: (next ? 1 : -1) * track.clientWidth, behavior: 'smooth' });
      }
    }

    onChange(event) {
      const variantSelect = event.target.closest('[data-leni-variant]');
      if (variantSelect) {
        const item = variantSelect.closest('[data-line]');
        const quantity = Number(item.querySelector('.leni-cart__qty-value')?.textContent || 1);
        this.request('cart/update.js', {
          updates: { [item.dataset.key]: 0, [variantSelect.value]: quantity },
        });
        return;
      }

      const recSelect = event.target.closest('[data-leni-rec-variant]');
      if (recSelect) {
        const rec = recSelect.closest('[data-leni-rec]');
        const option = recSelect.selectedOptions[0];
        const price = rec.querySelector('[data-leni-rec-price]');
        const compare = rec.querySelector('[data-leni-rec-compare]');
        price.textContent = option.dataset.price;
        price.classList.toggle('is-sale', Boolean(option.dataset.compare));
        compare.textContent = option.dataset.compare || '';
        rec.querySelector('[data-leni-rec-add]').dataset.leniRecAdd = recSelect.value;
      }
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-leni-discount-form]');
      if (!form) return;
      event.preventDefault();

      const input = form.querySelector('input[name="discount"]');
      const code = input.value.trim();
      if (!code) return;

      const codes = [...new Set([...this.currentCodes(), code])];
      const data = await this.request('cart/update.js', { discount: codes.join(',') });
      if (!data) return;

      const applied = (data.discount_codes || []).find((entry) => entry.code.toLowerCase() === code.toLowerCase());
      if (!applied || !applied.applicable) {
        // De drawer is net opnieuw gerenderd: zoek de nieuwe elementen op.
        const details = this.drawer.querySelector('.leni-cart__discount');
        const error = this.drawer.querySelector('[data-leni-discount-error]');
        if (details) details.open = true;
        if (error) {
          error.textContent = `"${code}" can't be applied to your cart.`;
          error.hidden = false;
        }
        // Een ongeldige code blijft anders in de cart staan; weer weghalen.
        if (applied) {
          const valid = (data.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);
          await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: valid.join(',') }),
          });
        }
      }
    }

    currentCodes() {
      return [...this.querySelectorAll('[data-leni-remove-code]')].map((button) => button.dataset.leniRemoveCode);
    }

    /* --- Gratis-verzendingsbalk in andere valuta ------------------------ */
    /* Liquid rekent in de winkelvaluta (EUR). Betaalt de klant in een andere
       valuta, dan schalen we de drempel met de wisselkoers van Shopify. */

    adjustShippingForCurrency() {
      const currency = window.Shopify?.currency;
      const box = this.querySelector('[data-leni-shipping]');
      if (!box || !currency || Number(currency.rate) === 1) return;

      const threshold = Math.round(Number(this.dataset.threshold) * Number(currency.rate));
      const total = Number(this.dataset.total);
      const remaining = threshold - total;
      const text = box.querySelector('[data-leni-shipping-text]');
      const bar = box.querySelector('[data-leni-shipping-bar]');

      if (remaining > 0) {
        const amount = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency.active,
        }).format(remaining / 100);
        text.innerHTML = `You&rsquo;re <strong>${amount}</strong> away from Free Shipping`;
        bar.style.width = `${Math.min(100, (total / threshold) * 100)}%`;
      } else {
        text.innerHTML = '<strong>You&rsquo;ve unlocked Free Shipping!</strong>';
        bar.style.width = '100%';
      }
    }

    /* --- Timer ------------------------------------------------------------ */

    startTimer() {
      const minutes = Number(this.dataset.timer);
      const display = this.querySelector('[data-leni-timer]');
      if (!minutes || !display) return;

      const duration = minutes * 60 * 1000;
      let end = Number(storage.get(TIMER_KEY));
      if (!end || end <= Date.now()) {
        end = Date.now() + duration;
        storage.set(TIMER_KEY, String(end));
      }

      const tick = () => {
        let left = end - Date.now();
        if (left <= 0) {
          end = Date.now() + duration;
          storage.set(TIMER_KEY, String(end));
          left = duration;
        }
        const total = Math.ceil(left / 1000);
        const mm = String(Math.floor(total / 60)).padStart(2, '0');
        const ss = String(total % 60).padStart(2, '0');
        display.textContent = `${mm}:${ss}`;
      };
      tick();
      this.timerId = setInterval(tick, 1000);
    }

    /* --- Complete your look ---------------------------------------------- */

    async loadRecs() {
      const url = this.dataset.recsUrl;
      const box = this.querySelector('[data-leni-recs]');
      const track = this.querySelector('[data-leni-recs-track]');
      if (!url || !box || !track) return;

      const key = `${url}|${this.dataset.recsKey}`;
      try {
        let html = recsCache.get(key);
        if (!html) {
          const response = await fetch(url);
          if (!response.ok) return;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          html = doc.querySelector('[data-leni-recs-pages]')?.innerHTML.trim() || '';
          recsCache.set(key, html);
        }
        if (!html) return;
        track.innerHTML = html;
        box.hidden = false;
        const pages = track.querySelectorAll('.leni-recs__page').length;
        box.querySelector('.leni-recs__nav').hidden = pages < 2;
      } catch (error) {
        /* Geen aanbevelingen is geen fout; het blok blijft verborgen. */
      }
    }
  }

  customElements.define('leni-cart', LeniCart);
})();
